import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { useToast } from '../components/ui/toast';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Video, Mail, Lock, Eye, EyeOff } from 'lucide-react';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const login = useAuthStore((state) => state.login);
  const { addToast } = useToast();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      addToast({
        title: "Welcome back!",
        description: "You have successfully signed in.",
        variant: "success"
      });
      navigate('/dashboard/meetings');
    } catch (error: any) {
      addToast({
        title: "Login failed",
        description: error.response?.data?.message || 'Invalid credentials. Please try again.',
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen auth-bg flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        {/* Logo and branding */}
        <div className="text-center space-y-4 fade-in">
          <img 
            src="/logo.svg" 
            alt="LiveKit Logo" 
            className="mx-auto h-24 w-24"
          />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Welcome back</h1>
            <p className="text-muted-foreground mt-2">Sign in to your LiveKit account</p>
          </div>
        </div>

        {/* Login form */}
        <Card className="slide-in shadow-2xl border-0 bg-card/50 backdrop-blur-sm">
          <form onSubmit={handleSubmit}>
            <CardHeader className="space-y-1 pb-4">
              <CardTitle className="text-2xl text-center font-semibold">Sign in</CardTitle>
              <CardDescription className="text-center">
                Enter your credentials to access your account
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <Input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={loading}
                  icon={<Mail className="h-4 w-4" />}
                  className="h-12"
                />
                
                <Input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  icon={<Lock className="h-4 w-4" />}
                  className="h-12"
                />
              </div>

              <div className="flex items-center justify-between">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input type="checkbox" className="rounded border-border" />
                  <span className="text-sm text-muted-foreground">Remember me</span>
                </label>
                {/* <Link 
                  to="#" 
                  className="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </Link> */}
              </div>
            </CardContent>
            
            <CardFooter className="flex flex-col space-y-4 pt-6">
              <Button 
                type="submit" 
                className="w-full h-12 text-base"
                disabled={loading}
                loading={loading}
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
              
          
              
              
              <p className="text-center text-sm text-muted-foreground">
                Forgot your password?{' '}
                <Link 
                  to="/forgot-password" 
                  className="font-medium text-primary hover:text-primary/80 transition-colors"
                >
                  Reset password
                </Link>
              </p>
            </CardFooter>
          </form>
        </Card>
        
        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground fade-in">
          <p>© 2024 LiveKit. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;
